import type { GameEvent, Member, Reaction, ServerMessage, Snapshot } from "@app/protocol";
import type { DiscordSDK } from "@discord/embedded-app-sdk";
import { create } from "zustand";
import type { HandshakeDiagnostics, HandshakeError, HandshakeStage } from "./discord/handshake";
import { factsOf } from "./variants/facts";
import type { Connection } from "./ws/client";

export type Phase = "authorizing" | "failed" | "ready";

/**
 * A deal to animate right now: the match started or a new hand was dealt ("full"),
 * Baloot dealt the rest after the call ("rest"), or Pişti's hands were filled again from
 * the deck ("top-up", ADR 0050). Only live changes set it, so a reload or a late join
 * never replays a deal.
 */
export interface DealCue {
  at: number;
  kind: "full" | "rest" | "top-up";
}

/** The deal an event announces, if it announces one. */
function dealOf(events: readonly { code: string }[]): DealCue["kind"] | null {
  if (events.some((e) => e.code === "hand-dealt")) return "full";
  return events.some((e) => e.code === "cards-dealt") ? "top-up" : null;
}

export interface AppState {
  phase: Phase;
  error: HandshakeError | null;
  stage: HandshakeStage;
  /** Filled only when `ready` timed out; shown on the failure screen. */
  diagnostics: HandshakeDiagnostics | null;
  /** The reported handshake failure, shown so the player can quote it (review 0019 S8). */
  eventId: string | null;
  member: Member | null;
  guildId: string | null;
  /** Our session token, for plain API calls (the open tables list). */
  session: string | null;
  /** This channel's own table; joining another table keeps it as the way back. */
  instanceId: string | null;
  /** The table the socket is on right now. */
  tableId: string | null;
  /** The Discord SDK once ready; null in mock mode. External links must go through it. */
  sdk: DiscordSDK | null;
  connection: Connection;
  /** The server speaks a protocol this build cannot read; only a reload helps (review 0027 C1). */
  stale: boolean;
  /** When `connection` last changed; the reconnect veil waits a moment from here. */
  connectionAt: number;
  /** You asked to leave: the server closing the table then is no lost connection. Cleared by the next table. */
  leaving: boolean;
  snapshot: Snapshot | null;
  /** Last rule or table error code from the server; cleared by the next snapshot. */
  lastError: string | null;
  events: GameEvent[];
  /** When the latest events batch arrived; the band shows fresh ones briefly. */
  eventsAt: number;
  deal: DealCue | null;
  /** Reactions heard lately, newest last; each shows by its pod for a moment (review 0022). */
  reactions: ShownReaction[];
  /** Players whose reactions and gifts you hid for this session (decision 143). */
  muted: string[];
  /** Gifts hidden on this device (decision 154). */
  hideGifts: boolean;
  /** Players talking in the voice channel right now (decision 146); empty without the scope. */
  speaking: string[];
}

export interface ShownReaction {
  from: string;
  reaction: Reaction;
  at: number;
}

/** Gifts hidden on this device (decision 154); read once, written by My data. */
export const HIDE_GIFTS_KEY = "hcc:hide-gifts";

function storedFlag(key: string): boolean {
  try {
    return globalThis.localStorage?.getItem(key) === "1";
  } catch {
    return false;
  }
}

export const initialState: AppState = {
  phase: "authorizing",
  error: null,
  stage: "ready",
  diagnostics: null,
  eventId: null,
  member: null,
  guildId: null,
  session: null,
  instanceId: null,
  tableId: null,
  sdk: null,
  connection: "closed",
  stale: false,
  connectionAt: 0,
  leaving: false,
  snapshot: null,
  lastError: null,
  events: [],
  eventsAt: 0,
  deal: null,
  reactions: [],
  muted: [],
  hideGifts: storedFlag(HIDE_GIFTS_KEY),
  speaking: [],
};

type Stage = { variant?: string; phase?: string } | null | undefined;

/** The table left the lobby for the game: the first hand is being dealt right now. */
function matchStarted(prev: Snapshot | null, next: Snapshot): boolean {
  const waiting = prev?.table.status === "lobby" || prev?.table.status === "countdown";
  return waiting && next.table.status === "playing";
}

/** Baloot deals the rest of the hand once the call is over. */
function restDealt(prev: Snapshot | null, next: Snapshot): boolean {
  const was = prev?.view as Stage;
  const is = next.view as Stage;
  const calling = was?.phase === "bidding" || was?.phase === "doubling";
  return factsOf(is?.variant)?.dealsRestAfterCall === true && is?.phase === "playing" && calling;
}

function dealCue(prev: Snapshot | null, next: Snapshot, now: number): DealCue | null {
  if (matchStarted(prev, next)) return { at: now, kind: "full" };
  return restDealt(prev, next) ? { at: now, kind: "rest" } : null;
}

/** Pure transition for a server message; tested without React. */
export function reduceMessage(state: AppState, message: ServerMessage): AppState {
  const now = Date.now();
  switch (message.type) {
    case "snapshot":
      return {
        ...state,
        snapshot: message,
        lastError: null,
        leaving: false,
        deal: dealCue(state.snapshot, message, now) ?? state.deal,
      };
    case "events": {
      const dealt = dealOf(message.events);
      return {
        ...state,
        events: [...state.events, ...message.events].slice(-50),
        eventsAt: now,
        // Every hand after the first announces its deal (the first one starts the match).
        deal: dealt === null ? state.deal : { at: now, kind: dealt },
      };
    }
    case "error":
      return { ...state, lastError: message.code };
    case "reaction":
      return {
        ...state,
        reactions: [
          ...state.reactions,
          { from: message.from, reaction: message.reaction, at: now },
        ].slice(-12),
      };
    default:
      return state;
  }
}

export const useStore = create<
  AppState & { apply: (m: ServerMessage) => void; set: (p: Partial<AppState>) => void }
>((set) => ({
  ...initialState,
  apply: (message) => set((state) => reduceMessage(state, message)),
  set: (patch) => set(patch),
}));
